import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { FiGlobe, FiUsers, FiLock, FiRepeat } from "react-icons/fi";
import TextPost from "./PostTypes/TextPost";
import PhotoPost from "./PostTypes/PhotoPost";
import ResearchPost from "./PostTypes/ResearchPost";
import RepostPost from "./PostTypes/RepostPost";
import QuestionPost from "./PostTypes/QuestionPost";
import CollaborationPost from "./PostTypes/CollaborationPost";
import PollPost from "./PostTypes/PollPost";
import MilestonePost from "./PostTypes/MilestonePost";
import PostActions from "./PostActions";
import PostMenu from "./PostMenu";
import CommentSection from "./CommentSection";
import UserAvatar from "./UserAvatar";
import VerifiedBadge from "./VerifiedBadge";
import ConfirmModal from "./ConfirmModal";
import FollowButton from "../common/FollowButton";
import { postApi } from "../../services/api/postApi";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import { usePostViewTracking } from "../../hooks/usePostViewTracking";
import "./FeedPost.css";

// Visibility icons and labels
const visibilityConfig = {
  public: { icon: FiGlobe, label: "Public" },
  connections: { icon: FiUsers, label: "Friends" },
  private: { icon: FiLock, label: "Private" },
};

// Comment section animation
const commentVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 },
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const FeedPost = ({ post, onUpdate, onDelete, onFollowChange }) => {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [reposting, setReposting] = useState(false);

  // Track when post is visible in viewport (70% threshold)
  const { elementRef, isVisible } = useIntersectionObserver({
    threshold: 0.7,
    triggerOnce: false,
  });

  // Track view and reading time
  usePostViewTracking(post._id, isVisible, {
    minReadTime: 3,
    enabled: true,
  });

  // Get visibility config
  const visibility =
    visibilityConfig[post.visibility] || visibilityConfig.public;
  const VisibilityIcon = visibility.icon;

  // Render post content based on type
  const renderPostContent = () => {
    switch (post.postType) {
      case "text":
        return <TextPost post={post} />;
      case "photo":
        return <PhotoPost post={post} onUpdate={onUpdate} />;
      case "research":
        return <ResearchPost post={post} onUpdate={onUpdate} />;
      case "question":
        return <QuestionPost post={post} onUpdate={onUpdate} />;
      case "collaboration":
        return <CollaborationPost post={post} onUpdate={onUpdate} />;
      case "repost":
        return <RepostPost post={post} onUpdate={onUpdate} />;
      case "poll":
        return <PollPost post={post} onUpdate={onUpdate} />;
      case "milestone":
        return <MilestonePost post={post} />;
      default:
        return <TextPost post={post} />;
    }
  };

  // Question posts have their own self-contained layout
  const isQuestionPost = post.postType === "question";

  // Handle repost
  const handleRepost = async () => {
    setReposting(true);
    try {
      await postApi.repost(post._id);
      setShowRepostModal(false);
    } catch (error) {
      // Error handled silently
    } finally {
      setReposting(false);
    }
  };

  // Navigate to post detail page
  const handleGoToPost = () => {
    navigate(`/posts/${post._id}`);
  };

  return (
    <article ref={elementRef} className="cbx-post">
      {/* Post Header - Clickable to navigate to detail */}
      <header
        className="cbx-post-header cbx-post-header--clickable"
        onClick={handleGoToPost}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleGoToPost()}
      >
        <div className="cbx-post-author">
          {/* Avatar */}
          <div className="cbx-author-avatar">
            <div className="cbx-author-avatar-ring" />
            <UserAvatar
              user={post.author}
              size="large"
              className="cbx-author-avatar-img"
            />
          </div>

          {/* Author Info */}
          <div className="cbx-author-info">
            <div className="cbx-author-row">
              <div className="cbx-author-name-group">
                <h3 className="cbx-author-name">
                  {post.author?.name || "Unknown User"}
                </h3>
                <VerifiedBadge user={post.author} />
              </div>

              {/* Meta - Time & Visibility + Follow Button (Desktop Inline) */}
              <div className="cbx-post-meta">
                <span className="cbx-post-time">
                  {formatDistanceToNow(new Date(post.createdAt), {
                    addSuffix: true,
                  })}
                </span>
                <span className="cbx-meta-dot" />
                <span className="cbx-post-visibility">
                  <VisibilityIcon className="cbx-visibility-icon" />
                  <span>{visibility.label}</span>
                </span>
                
                {/* Follow Button - Desktop inline position */}
                <div className="cbx-follow-inline" onClick={(e) => e.stopPropagation()}>
                  <span className="cbx-meta-dot cbx-meta-dot--follow" />
                  <FollowButton
                    authorId={post.author?._id}
                    isFollowing={post.isFollowingAuthor || false}
                    isOwnPost={post.isOwnPost || false}
                    onFollowChange={onFollowChange}
                  />
                </div>
              </div>
            </div>

            {/* Professional Role + Institution (inline with dot separator) */}
            {(post.author?.professionalRole || (post.author?.institution?.verified && post.author?.institution?.name)) && (
              <p className="cbx-author-role">
                {post.author?.professionalRole}
                {post.author?.professionalRole && post.author?.institution?.verified && post.author?.institution?.name && ' · '}
                {post.author?.institution?.verified && post.author?.institution?.name}
              </p>
            )}
          </div>
        </div>

        {/* Post Menu (Edit/Delete) + Mobile Follow Button - Stop propagation */}
        <div className="cbx-post-actions-row" onClick={(e) => e.stopPropagation()}>
          {/* Follow Button - Mobile only (hidden on desktop via CSS) */}
          <div className="cbx-follow-mobile">
            <FollowButton
              authorId={post.author?._id}
              isFollowing={post.isFollowingAuthor || false}
              isOwnPost={post.isOwnPost || false}
              onFollowChange={onFollowChange}
            />
          </div>
          <PostMenu post={post} onDelete={onDelete} onUpdate={onUpdate} />
        </div>
      </header>

      {/* Post Content */}
      <div className="cbx-post-content">{renderPostContent()}</div>

      {/* Question posts get ONLY repost button */}
      {isQuestionPost && (
        <div className="cbx-question-actions">
          <motion.button
            className="cbx-question-repost-btn"
            onClick={() => setShowRepostModal(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiRepeat />
            <span>Repost Question</span>
          </motion.button>

          {/* Repost Confirmation Modal */}
          <ConfirmModal
            isOpen={showRepostModal}
            onClose={() => setShowRepostModal(false)}
            onConfirm={handleRepost}
            title="Repost Question?"
            message="Share this question with your followers on your feed?"
            confirmText="Repost"
            cancelText="Cancel"
            type="info"
            loading={reposting}
          />
        </div>
      )}

      {/* Non-question posts get full interaction */}
      {!isQuestionPost && (
        <>
          {/* Post Stats */}
          <div className="cbx-post-stats">
            <div className="cbx-stats-left">
              <div className="cbx-stats-likes">
                <span className="cbx-likes-count">
                  {post.likeCount || 0} likes
                </span>
              </div>
            </div>
            <div className="cbx-stats-right">
              <span
                className="cbx-stat-item cbx-stat-item--comments"
                onClick={() => setShowComments(!showComments)}
              >
                {post.commentCount || 0} comments
              </span>
              <span className="cbx-stat-item cbx-stat-item--shares">
                {post.repostCount || 0} shares
              </span>
            </div>
          </div>

          {/* Post Actions */}
          <PostActions
            post={post}
            onUpdate={onUpdate}
            onCommentClick={() => setShowComments(!showComments)}
          />

          {/* Comments Section - Animated */}
          <AnimatePresence>
            {showComments && (
              <motion.div
                variants={commentVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <CommentSection
                  postId={post._id}
                  commentCount={post.commentCount || 0}
                  onUpdate={onUpdate}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </article>
  );
};

export default FeedPost;
